import { DocAccessType, TBTestList, TBTestMap } from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import { SummaryRow } from '../../../Summary/SummaryRow';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: TBTestMap;
  warning?: string;
  userRole?: UserRole;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  onResume: (url: string) => void;
  onUploadClick: (docKey: string) => void;
  onOpenClick: (docKey: string) => void;
  onApproveClick: (docKey: string) => void;
  onRejectClick: (docKey: string) => void;
  onRescindRejectClick: (docKey: string) => void;
  onDeleteClick: (docKey: string) => void;
  onAcknowledgeClick: (docKey: string) => void;
}

const TBTestHand = (props: IProps) => {
  const { documents: docs = {}, infoOnly, userAccess, userRole } = props;

  let hasDocs = false;
  let hasDeletedDocs = false;
  const visibleDocs = getVisible({ documentsHash: docs, userAccess, infoOnly, userRole }) as TBTestList[];

  if (visibleDocs.length > 0) {
    hasDocs = true;
  }
  if (hasDeleted(docs)) {
    hasDeletedDocs = true;
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id={'tb-test-doc'}
      label={`You will need to take a TB test and upload the results`}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('personal-info/step-3');
      }}
      warning={props.warning}
    >
      {visibleDocs.length > 0 ? (
        visibleDocs.map((doc: TBTestList, index: number) => {
          const { key, item } = doc;
          return (
            <FingerWithApprove
              key={`tb-test-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              label={`Tuberculosis Test Results${
                Object.keys(docs).length > 1 ? (index > 0 ? ` ${index + 1}` : '') : ''
              }`}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onUploadClick={() => props.onUploadClick(key)}
              onOpenClick={() => props.onOpenClick(key)}
              onApproveClick={() => props.onApproveClick(key)}
              onRejectClick={() => props.onRejectClick(key)}
              onRescindRejectClick={() => props.onRescindRejectClick(key)}
              onDeleteClick={() => props.onDeleteClick(key)}
              onAcknowledgeClick={() => props.onAcknowledgeClick(key)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              {!infoOnly ? (
                <>
                  <SummaryRow
                    key={`tbtest-pending-${key}`}
                    label="Applied for TB Test"
                    value={item.hasOwnProperty('isTBTestPending') ? (item.isTBTestPending ? 'Yes' : 'No') : ''}
                  />

                  <SummaryRow
                    key={`tbtest-returningUKResident-${key}`}
                    label="Returning UK resident"
                    fullLabel={'Are you a returning UK resident?'}
                    value={
                      item.hasOwnProperty('isReturningUKResident') ? (item.isReturningUKResident ? 'Yes' : 'No') : ''
                    }
                  />

                  {item.isReturningUKResident ? (
                    <SummaryRow
                      key={`tbtest-awayFromUKMoreThan2Yrs-${key}`}
                      label="Been away from the UK over 2 years"
                      fullLabel={'Have you been away from the UK for more than 2 years?'}
                      value={
                        item.hasOwnProperty('awayFromUKMoreThan2Yrs')
                          ? item.awayFromUKMoreThan2Yrs
                            ? 'Yes'
                            : 'No'
                          : ''
                      }
                    />
                  ) : null}

                  <SummaryRow
                    key={`tbtest-hasLivedInExemptCountry-${key}`}
                    label="Lived in TB exempt country"
                    fullLabel={
                      'Have you lived for at least 6 months in a country where TB screening is not required by the UK?'
                    }
                    value={
                      item.hasOwnProperty('hasLivedInExemptCountry')
                        ? item.hasLivedInExemptCountry
                          ? 'Yes'
                          : 'No'
                        : ''
                    }
                  />

                  {item.hasLivedInExemptCountry ? (
                    <SummaryRow
                      key={`tbtest-hasStayedInExemptContry-${key}`}
                      label="Been away longer than 6 months"
                      fullLabel={'Have you been away from that country for more than 6 months?'}
                      value={
                        item.hasOwnProperty('hasStayedInExemptCountry')
                          ? item.hasStayedInExemptCountry
                            ? 'Yes'
                            : 'No'
                          : ''
                      }
                    />
                  ) : null}
                </>
              ) : null}
            </FingerWithApprove>
          );
        })
      ) : (
        <NoDocuments />
      )}
    </Section>
  );
};

export default TBTestHand;
